import {autoinject, transient, computedFrom, observable, BindingEngine, useView} from "aurelia-framework";
import {DataType, SourceType, QueryFilter, DataGridColumn} from "../../../../../../models/queries/queries.models";
import {LookupValueQuery} from "../../../../../../constants/modules/lookup.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import {Claims} from "../../../../../../constants/authentication/claims";
import {UserClaimsService} from "../../../../../../services/authentication/user-claims.service";
import {I18N} from "aurelia-i18n";
import {RulesClientService} from "../../../../../../services/rules/rules-client.service";
import {BusinessUnitFilteringModel, CustomerModel} from "../../../../../../models/customers/customer.models";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { Guid } from "../../../../../../utilities/guid";
import { MobileHelper } from "../../../../../../helpers/mobile-helper";
import { CustomGridPageBase } from "../../../../common/custom-grid-page.base";
import { GetBuRuleCollectionModel, type RuleCollectionModel } from "../../../../../../models/rules/rules.models";
import { Reflection } from "../../../../../../utilities/reflection";

@autoinject
@transient()
@useView('views/pages/modules/customers/steps/rules/rule-configs.html')
export class BusinessUnitRuleConfigs extends CustomGridPageBase {

    public branchId: number;
    public businessUnits: BusinessUnitFilteringModel[];
    public ruleCollections: GetBuRuleCollectionModel[];
    public canDragDrop: boolean = false;
    public isReordering: boolean = false;
    public customerId: number;

    @observable
    public selectedBusinessUnitToReorder: any;

    @observable
    public selectedRuleCollectionToReorder: RuleCollectionModel;
    
    @observable
    public selectedBusinessUnitIds: number[];

    @observable
    public selectedRuleCategoryIds: number[];

    @observable
    searchFilter: string = null;

    @computedFrom("dataGridVM", "dataGridVM.toggleAll", "dataGridVM._specificIds", "dataGridVM._specificIds.length")
    get anySelected(): boolean
    {
        return this.dataGridVM && ((this.dataGridVM.getSpecificIds() && this.dataGridVM.getSpecificIds().length > 0) || this.dataGridVM.toggleAll)
    }

    @computedFrom("dataGridVM", "dataGridVM.toggleAll", "dataGridVM._specificIds", "dataGridVM._specificIds.length")
    get anySelectedRisk(): boolean
    {
        const items = this.dataGridVM ? this.dataGridVM.getSelectedRowsCollection() : [];
        if(this.anySelected && items.length == 1){
            const item = items[0].item;
            return item.riskCategoryId;
        }
        
       return false;
    }

    selectedBusinessUnitIdsChanged() {
        this.reload();
    }

    selectedRuleCategoryIdsChanged() {
        this.reload();
    }

    constructor(protected i18n: I18N, protected bindingEngine: BindingEngine, private dialogClientService: DialogClientService, private userClaimsService: UserClaimsService, private rulesService: RulesClientService) {
        super(i18n, "Rules", bindingEngine)
    }

    activate(args: any) {
        if(args && Number(args.id))
            this.customerId = Number(args.id);
        if (args && Number(args.branchId))
            this.branchId = Number(args.branchId);
        if (args && args.customers && args.customers.length > 0)
        {
            const customer: CustomerModel = args.customers[0];
            this.businessUnits = <BusinessUnitFilteringModel[]>customer.businessUnits;
            //this is fallback for customerId
            if(!this.customerId && customer)
                this.customerId = customer.customerId;
        }

        this.reload();
    }

    getColumns() : DataGridColumn[]
    {
        return [
            {
                dataType: DataType.CustomTemplate,
                value: `<check-box checked.bind="getRowItem().isSelected"
                            changed.delegate="getDataGrid().selectRowInMultipleSelection(getRowItem())">
                        </check-box>`,
                name: "select",
                title: MobileHelper.isMobileDevice() ? "" : `<check-box class="check-box-select-all" checked.two-way="toggleAll"></check-box>`,
                parameter: {
                    item: null
                },
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -2,
                width: MobileHelper.isMobileDevice() ? "" : "38px",
                isHidden: false,
                isSortable: false,
                disableSorting: true,
                getCellClass: () => {
                    return "selector-checkbox";
                }
            },
            {
                sortOrder: 1,
                columnOrder: 0,
                name: "sequenceNumber",
                dataType: DataType.Number,
                title: "Order number",
                isFilterable: false,
                disableSorting: true,
                isSortable: false
            },
            {
                sortOrder: 1,
                columnOrder: 1,
                name: "ruleCollectionDisplayName",
                dataType: DataType.Default,
                title: "Rule Collection",
                isFilterable: false,
                disableSorting: true,
                isSortable: false
            },
            {
                sortOrder: 2,
                columnOrder: 2,
                name: "ruleCode",
                dataType: DataType.Default,
                title: "Rule code",
                isFilterable: false,
                disableSorting: true,
                isSortable: false
            },
            {
                sortOrder: 3,
                columnOrder: 3,
                name: "ruleName",
                dataType: DataType.Default,
                title: "Rule name",
                isFilterable: false,
                disableSorting: true,
                isSortable: false
            },
            {
                sortOrder: 4,
                columnOrder: 4,
                name: "ruleCategory",
                dataType: DataType.Default,
                title: "Rule category",
                isFilterable: false,
                disableSorting: true,
                isSortable: false
            },
            {
                sortOrder: 5,
                columnOrder: 5,
                name: "screeningType",
                dataType: DataType.Default,
                title: "Screening Type",
                isFilterable: false,
                isSortable: true
            },
            {
                sortOrder: 6,
                columnOrder: 6,
                name: "description",
                dataType: DataType.Default,
                title: "Description",
                isFilterable: false,
                disableSorting: true,
                isSortable: false,
                width: "350px"
            },
            {
                sortOrder: 7,
                columnOrder: 7,
                name: "businessUnitName",
                dataType: DataType.Default,
                title: "Business unit",
                isFilterable: false,
                isSortable: true
            },
            {
                sortOrder: 8,
                columnOrder: 8,
                name: "vettingCommentAction",
                dataType: DataType.Default,
                title: "Vetting comment action",
                isFilterable: false,
                isSortable: true
            }
            // ,
            // {
            //     sortOrder: 9,
            //     columnOrder: 9,
            //     name: "riskCategory",
            //     dataType: DataType.Default,
            //     title: "Risk Category",
            //     isFilterable: false,
            //     isSortable: true
            // },
            // {
            //     sortOrder: 10,
            //     columnOrder: 10,
            //     name: "riskLevel",
            //     dataType: DataType.Default,
            //     title: "Risk Level",
            //     isFilterable: false,
            //     isSortable: true
            // }
        ];
    }

    getInitialSorting() {
        const columns: DataGridColumn[] = [];
        columns.push(
            <DataGridColumn>{
                sortOrder: -1,
                columnOrder: -1,
                name: "sequenceNumber",
                dataType: DataType.Number,
                title: "Order",
                isFilterable: false,
                isSortable: true,
                isAscending: true,
                isHidden: true,
            }
        );
        return columns;
    }

    reload() {

        const filters = [];

        if (this.selectedBusinessUnitToReorder && this.selectedRuleCollectionToReorder) {
            const filter1 = QueryFilter.getQuery("businessUnitId", this.selectedBusinessUnitToReorder.id, "eq", DataType.Number, true);
            filters.push(filter1);
            const filter2 = QueryFilter.getQuery("ruleCollectionId", this.selectedRuleCollectionToReorder.ruleCollectionId, "eq", DataType.Number, true);
            filters.push(filter2);
        } 
        else {
            if (this.selectedBusinessUnitIds && this.selectedBusinessUnitIds.length > 0)
            {
                const filter = QueryFilter.getQuery("businessUnitId", this.selectedBusinessUnitIds, '{0} in ({1})', DataType.Lookup, true, false, null, null, true);
                filter.filterOperator.friendlyName = "in";
                filter.filterProperty.lookupMappedToProperty = LookupValueQuery.BusinessUnits;
                filters.push(filter);
            }
            else
                filters.push(QueryFilter.getQuery("branchId", this.branchId, 'eq', DataType.Number));
    
            if (this.selectedRuleCategoryIds && this.selectedRuleCategoryIds.length > 0)
            {
                const filter = QueryFilter.getQuery("ruleCategoryId", this.selectedRuleCategoryIds, '{0} in ({1})', DataType.Lookup, true, false, null, null, true);
                filter.filterOperator.friendlyName = "in";
                filter.filterProperty.lookupMappedToProperty = LookupValueQuery.RuleCategories;
                filters.push(filter);
            }
    
            if(this.searchFilter)
            {
                const guid = Guid.generateGuid();
                filters.push(QueryFilter.getQuery("ruleName", this.searchFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
                filters.push(QueryFilter.getQuery("description", this.searchFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
                filters.push(QueryFilter.getQuery("ruleCode", this.searchFilter, "contains({0}, '{1}')", DataType.Default, false, true, guid, true, true));
                filters.push(QueryFilter.getQuery("screeningType", this.searchFilter, "contains({0}, '{1}')", DataType.Default, false, true, guid, true, true));
            }
        }

        
        if (this.selectedStatusFilter && this.selectedStatusFilter.length > 0) {
            this.selectedStatusFilter.forEach(x => {
                filters.push(QueryFilter.getQuery("statusId", x, "eq", DataType.Number, false, false, "statusGroup", true));
            });
        }

        let filteTriggered = false;
        if (this.softRulesFilterSelected){
            filters.push(QueryFilter.getQuery("isSoftRule", true, "eq", DataType.Boolean, !filteTriggered, false, "rulesGroup", true));
            filteTriggered = true;
        }
        if (this.criticalRulesFilterSelected) {
            filters.push(QueryFilter.getQuery("isCriticalRule", true, "eq", DataType.Boolean, !filteTriggered, false, "rulesGroup", true));
            filteTriggered = true;
        }
        if (this.vettingRulesFilterSelected) {
            filters.push(QueryFilter.getQuery("isVettingRule", true, "eq", DataType.Boolean, !filteTriggered, false, "rulesGroup", true));
            filteTriggered = true;
        }
        if (this.vetAutoScrenningRulesFilterSelected) {
            filters.push(QueryFilter.getQuery("isVetAutoScreeningRule", true, "eq", DataType.Boolean, !filteTriggered, false, "rulesGroup", true));
            filteTriggered = true;
        }

        
        if (this.riskCategoryFilterActive) {

            const riskCategoryFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "riskCategoryId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedRiskCategories || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            filters.push(riskCategoryFilter);
        }


        this.itemSource = {
            columns: this.getColumns(),
            source: "/odata/BusinessUnitRules",
            sourceType: SourceType.ODataUrl,
            initialFilters: filters,
            initialSorting: this.getInitialSorting()
        }
    }

    showRiskCategoryFilter:boolean = false;
    selectedRiskCategories:number[];

    public toggleRiskCategoryFilters() {
        if (!this.showRiskCategoryFilter) {
            this.closeAllFilters();
        }
        this.showRiskCategoryFilter = !this.showRiskCategoryFilter;
        this.selectedFilter = {
            title: "Risk Category",
            clear: () => {
                this.clearRiskCategoryFilter();
            },
            apply: () => {
                this.applyRiskCategoryFilter(this.selectedRiskCategories.map(cId => {
                    return { id: cId };
                }), false);
            }
        }
    }

    public applyRiskCategoryFilter(items: any[], reload: boolean = true) {
        this.selectedRiskCategories = items.map(x => x.id);
        if (reload == true) this.reload();
        this.toggleRiskCategoryFilters();
    }

    public clearRiskCategoryFilter() {
        this.selectedRiskCategories = [];
        this.reload();
    }

    @computedFrom("selectedRiskCategories", "selectedRiskCategories.length")
    public get riskCategoryFilterActive() {
        return this.selectedRiskCategories && this.selectedRiskCategories.length > 0;
    }


    @computedFrom('itemSource')
    public get hasRemoveRuleAssignmentPermission(): boolean {
        if (!this.userClaimsService) return false;
        return this.userClaimsService.hasPermission(Claims.CanRemoveRuleAssignments);
    }

    @computedFrom('itemSource')
    public get canReorderBusinessUnitRuleConfigs(): boolean {
        if (!this.userClaimsService) 
            return false;
        return this.userClaimsService.hasPermission(Claims.CanReorderBusinessUnitRuleConfigs);
    }

    async setVettingCommentAction(action: number) {
        const ruleConfigs = this.dataGridVM.getSelectedRowsCollection().map(x => {
            return { businessUnitId:x.item.businessUnitId, ruleCollectionId:x.item.ruleCollectionId, ruleCollectionRuleConfigId: x.item.ruleCollectionRuleConfigId };
        });
        await this.rulesService.setVettingCommentAction(action , {
            ruleConfigs: ruleConfigs
        });
        this.dataGridVM.unselectAll();
        this.reload();
    }


    overrideVettingActions() {
        const ruleConfigs = this.dataGridVM.getSelectedRowsCollection().map(x => {
            return { businessUnitId:x.item.businessUnitId, ruleCollectionId: x.item.ruleCollectionId, ruleCollectionRuleConfigId: x.item.ruleCollectionRuleConfigId };
        });
        const vettingAction = this.dataGridVM.getSelectedRowsCollection()
        .filter(x => x.item.vettingActionOverride)    
        .map(x => x.item.vettingActionOverride)[0];
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.BusinessUnitRuleDialog, { ruleConfigs: ruleConfigs, vettingAction: vettingAction,
            reload: this.reload.bind(this)}, true, false, false, 'right');
    }

    overrideVettingRiskScore() {
        if(this.anySelectedRisk) {
            const ruleConfigs = this.dataGridVM.getSelectedRowsCollection().map(x => {
                return { businessUnitId:x.item.businessUnitId, ruleCollectionId: x.item.ruleCollectionId, ruleCollectionRuleConfigId: x.item.ruleCollectionRuleConfigId };
            });
            const item = this.dataGridVM.getSelectedRowsCollection()[0].item;
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.BusinessUnitRuleRiskDialog, { 
                ruleConfigs: ruleConfigs, 
                riskCategoryId: item.riskCategoryId, 
                defaultRiskLevel: item.defaultRiskLevel, 
                defaultRiskLevelId: item.defaultRiskLevelId, 
                overriddenRiskLevel: item.overriddenRiskLevel, 
                overriddenRiskLevelId: item.overriddenRiskLevelId, 
                reload: this.reload.bind(this)
            }, true, false, false, 'right');
        }
    }

    clearSearchFilter() {
        this.searchFilter = null;
    }

    searchFilterChanged()
    {
        this.reload()
    }

    onRuleCollectionToReorderChanged($event) {
        if ($event && $event.detail.item && this.canReorderBusinessUnitRuleConfigs) {
            this.canDragDrop = true;
            this.isReordering = true;
        }
        else {
            this.canDragDrop = false;
            this.isReordering = false;
            this.selectedBusinessUnitToReorder = null;
            this.selectedRuleCollectionToReorder = null;
        }

        this.reload();
    }

    async onBUToReorderChanged($event) {
        if ($event && $event.detail.value && this.canReorderBusinessUnitRuleConfigs) {
            this.ruleCollections = await this.rulesService.getBuRuleCollections(this.selectedBusinessUnitToReorder.id, null, true);
        }
        else {
            this.canDragDrop = false;
            this.isReordering = false;
            this.selectedBusinessUnitToReorder = null;
            this.selectedRuleCollectionToReorder = null;
        }
    }

    itemMoved($event): void{
        if (this.canDragDrop && $event && $event.detail && $event.detail.item.sequenceNumber >= 0) {
            const request = {
                ruleCollectionId: $event.detail.item.ruleCollectionId,
                ruleCollectionRuleConfigId: $event.detail.item.ruleCollectionRuleConfigId,
                businessUnitId: $event.detail.item.businessUnitId,
                sequenceNumber: $event.detail.toIndex + 1
            }
            this.rulesService.reorderBusinessUnitRules(request).then(() => {
                this.reload();
            })
        }
    }

    public searchKeyDown($event): void {
        if($event && $event.detail)
        {
            const code = $event.detail.keyCode;
            if (code === 13) {
                this.reload()
            }
        }
    }

    //#region Filters

    @observable()
    filter: string;
    
    public appliedFilter: string;
    public initialFilters: QueryFilter[];
    public selectedStatusFilter: number[] = [];
    public mobileSelectedFilter: number[] = [];
    public isMobileFilterOpen: boolean;
    public vettingRulesFilterSelected: boolean = false;
    public criticalRulesFilterSelected: boolean = false;
    public softRulesFilterSelected: boolean = false;
    public vetAutoScrenningRulesFilterSelected: boolean = false;
    public showColumnSorting: boolean = false;
    public selectedColumnSorting: number[] = [];
    public selectedFilter: { title: string, clear: () => void, apply: () => void };


    @computedFrom(Reflection.getPropertyPath<BusinessUnitRuleConfigs>(z => z.showColumnSorting),
    Reflection.getPropertyPath<BusinessUnitRuleConfigs>(z => z.showRiskCategoryFilter))
    public get isSubFilterOpen(): boolean {
        return this.showColumnSorting || this.showRiskCategoryFilter;
    }

    @computedFrom(Reflection.getPropertyPath<BusinessUnitRuleConfigs>(z => z.showColumnSorting))
    public get mobileFilterSelected() {
        return this.showColumnSorting;
    }


    public toggleColumnSortingFilters(){
        if (!this.showColumnSorting) {
            this.closeAllFilters();
        }
        this.showColumnSorting = !this.showColumnSorting;
        this.selectedFilter = {
            title: "Column sorting",
            clear: () => {
            },
            apply: () => {
                this.applyColumnSortingFilter(this.selectedColumnSorting.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyColumnSortingFilter(items: any[], reload: boolean = true) {
        this.selectedColumnSorting = items.map(x => x.id);
        if (reload == true) this.reload();
        this.toggleColumnSortingFilters();
    }

    public closeAllFilters() {
        this.showColumnSorting = false;
        this.showRiskCategoryFilter = false;
        return false;
    }

    filterVettingRules(isMobile: boolean = false) {
        this.vettingRulesFilterSelected = !this.vettingRulesFilterSelected;
        if (!isMobile) this.reload();
    }

    filterCriticalRules(isMobile: boolean = false) {
        this.criticalRulesFilterSelected = !this.criticalRulesFilterSelected;
        if (!isMobile) this.reload();
    }

    filterSoftRules(isMobile: boolean = false) {
        this.softRulesFilterSelected = !this.softRulesFilterSelected;
        if (!isMobile) this.reload();
    }

    filterVetAutoScreeningRules(isMobile: boolean = false) {
        this.vetAutoScrenningRulesFilterSelected = !this.vetAutoScrenningRulesFilterSelected;
        if (!isMobile) this.reload();
    }

    clearAllFilters(reload: boolean = true) {
        this.vettingRulesFilterSelected = false;
        this.softRulesFilterSelected = false;
        this.criticalRulesFilterSelected = false;
        this.vetAutoScrenningRulesFilterSelected = false;
        this.selectedRiskCategories = [];

        if (reload)
            this.reload();
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        this.showRiskCategoryFilter = false;
        this.showColumnSorting = false;
    }

    sortByMobile(column: DataGridColumn){
        this.dataGridVM.sortingTriggered(column);
    }

    public mobileFilterApply(toggleDialog: boolean = true) {
        if (toggleDialog)
            this.toggleMobileFilterDialog();

        this.appliedFilter = this.filter;
        this.selectedStatusFilter = [];
        const column = this.columns.filter(x => x.name == 'status')[0];
        column.filters = [];

        this.reload();
    }

  
    //#endregion
}
